import { Injectable } from '@angular/core';

/**
 * Holds the embed link behaviour pushed in from the host page (loader):
 * - parentOrigin: the origin of the parent website.
 * - sameOriginUseTop: when enabled, links whose origin matches parentOrigin are
 *   opened with target="_top" (navigate the host page) instead of target="_blank".
 */
@Injectable({ providedIn: 'root' })
export class EmbedLinkConfigService {
  parentOrigin = '';
  sameOriginUseTop = false;
  // Normalized origin (scheme + host + port) derived from parentOrigin. The raw
  // value may be a bare host ("host.com") or a full URL with a path/trailing
  // slash ("https://host.com/app/"); comparing against those directly would
  // never match new URL(...).origin, so we normalize once here.
  private normalizedOrigin = '';

  configure(parentOrigin: string | null | undefined, sameOriginUseTop: boolean): void {
    this.parentOrigin = parentOrigin || '';
    this.sameOriginUseTop = !!sameOriginUseTop;
    this.normalizedOrigin = this.toOrigin(this.parentOrigin);
  }

  private toOrigin(value: string): string {
    if (!value) return '';
    try {
      return new URL(value).origin;
    } catch (error) {
      // Missing scheme (e.g. "host.com") -> retry assuming https.
      try {
        return new URL('https://' + value).origin;
      } catch (innerError) {
        return '';
      }
    }
  }

  /**
   * Returns the anchor target for a link. "_top" when the feature is enabled and
   * the link's origin matches the parent origin, otherwise "_blank".
   */
  resolveTarget(url: string): string {
    if (this.sameOriginUseTop && this.normalizedOrigin && url) {
      try {
        if (new URL(url, this.normalizedOrigin).origin === this.normalizedOrigin) {
          return '_top';
        }
      } catch (error) {
        // Not an http(s) URL (tel:, mailto:, malformed) -> fall through to _blank.
      }
    }
    return '_blank';
  }
}
